import { clampPoint } from '../arena-runtime';
import { constrainGroundStep, type MovementResolution } from '../navigation/constraints';
import type { ArenaGameplayDefinition, BattleEvent, BattleState, CardId, EntityId, EntityState, PlayerId, ProjectileState, SpawnDefinition, WorldPoint } from '../types';
import type { MechanicEmitter, SimulationRecorder } from './system-context';

export interface ProjectileSystemContext {
  readonly state: BattleState;
  readonly arena: ArenaGameplayDefinition;
  readonly events: BattleEvent[];
  readonly emitMechanic: MechanicEmitter;
  readonly spawnDefinitionsAt: (owner: PlayerId, cardId: CardId, groups: readonly SpawnDefinition[], at: WorldPoint, deployTicks: number) => void;
  readonly applyDamage: (sourceId: EntityId | null, owner: PlayerId, cardId: CardId, visualKey: string, target: EntityState, damage: number, towerMultiplier: number) => void;
  readonly recordMovementResolution: (entity: EntityState, previous: WorldPoint, proposed: WorldPoint, resolution: MovementResolution, source: 'knockback') => void;
  readonly record: SimulationRecorder;
}

const distance = (left: WorldPoint, right: WorldPoint): number => Math.hypot(left.x - right.x, left.y - right.y);
const entityById = (state: BattleState, id: EntityId | null): EntityState | null => id === null ? null : state.entities.find((entity) => entity.id === id && !entity.removed) ?? null;

export function updateProjectiles(context: ProjectileSystemContext): void {
  for (const projectile of context.state.projectiles.sort((left, right) => left.id - right.id)) {
    if (projectile.dead) continue;
    if (projectile.behavior === 'line-damage') { updateLineProjectile(context, projectile); continue; }
    if (projectile.remainingTicks !== null) {
      projectile.remainingTicks -= 1;
      const total = Math.max(1, projectile.remainingTicks + 1);
      const next = clampPoint({ x: projectile.position.x + (projectile.targetPoint.x - projectile.position.x) / total, y: projectile.position.y + (projectile.targetPoint.y - projectile.position.y) / total }, context.arena.worldBounds);
      projectile.position.x = next.x;
      projectile.position.y = next.y;
      if (projectile.remainingTicks <= 0) impactProjectile(context, projectile);
      continue;
    }
    const target = entityById(context.state, projectile.targetId);
    if (target && !target.dead) projectile.targetPoint = { ...target.position };
    const dx = projectile.targetPoint.x - projectile.position.x;
    const dy = projectile.targetPoint.y - projectile.position.y;
    const length = Math.hypot(dx, dy);
    if (length <= projectile.speedPerTick) impactProjectile(context, projectile);
    else {
      projectile.position.x += Math.trunc((dx / length) * projectile.speedPerTick);
      projectile.position.y += Math.trunc((dy / length) * projectile.speedPerTick);
    }
  }
}

function impactProjectile(context: ProjectileSystemContext, projectile: ProjectileState): void {
  projectile.dead = true;
  context.events.push({ type: 'projectile-impacted', tick: context.state.tick, projectileId: projectile.id, visualKey: projectile.visualKey, at: { ...projectile.targetPoint } });
  if (projectile.mechanic === 'death-damage') context.emitMechanic(projectile.sourceId, projectile.sourceCardId, 'death-damage', 'impact', projectile.visualKey, projectile.targetPoint);
  if (projectile.behavior === 'line-damage') {
    context.record('PROJECTILE_IMPACT', { position: projectile.targetPoint, targetIds: projectile.hitEntityIds ?? [] }, { entityIds: [projectile.id, ...(projectile.hitEntityIds ?? [])], cardIds: [projectile.sourceCardId] });
    return;
  }
  if (projectile.behavior === 'spawn-units') {
    context.spawnDefinitionsAt(projectile.owner, projectile.sourceCardId, projectile.spawnGroups ?? [], projectile.targetPoint, 0);
    context.record('PROJECTILE_IMPACT', { position: projectile.targetPoint, spawnedGroups: projectile.spawnGroups ?? [] }, { entityIds: [projectile.id], cardIds: [projectile.sourceCardId] });
    return;
  }
  const targets = context.state.entities
    .filter((entity) => entity.owner !== projectile.owner && !entity.dead && !entity.removed && entity.deployRemaining <= 0)
    .filter((entity) => projectile.splashRadius > 0 ? distance(entity.position, projectile.targetPoint) <= projectile.splashRadius + entity.radius : entity.id === projectile.targetId)
    .sort((left, right) => left.id - right.id);
  for (const target of targets) context.applyDamage(projectile.sourceId, projectile.owner, projectile.sourceCardId, projectile.visualKey, target, projectile.damage, projectile.towerDamageMultiplier);
  context.record('PROJECTILE_IMPACT', { position: projectile.targetPoint, targetIds: targets.map((target) => target.id) }, { entityIds: [projectile.id, ...targets.map((target) => target.id)], cardIds: [projectile.sourceCardId] });
  if (projectile.mechanic === 'death-damage') context.emitMechanic(projectile.sourceId, projectile.sourceCardId, 'death-damage', 'complete', projectile.visualKey, projectile.targetPoint);
}

function updateLineProjectile(context: ProjectileSystemContext, projectile: ProjectileState): void {
  const dx = projectile.targetPoint.x - projectile.position.x;
  const dy = projectile.targetPoint.y - projectile.position.y;
  const length = Math.hypot(dx, dy);
  const amount = Math.min(projectile.speedPerTick, length);
  if (length > 0) {
    projectile.position.x += Math.round((dx / length) * amount);
    projectile.position.y += Math.round((dy / length) * amount);
  }
  const alreadyHit = new Set(projectile.hitEntityIds ?? []);
  const targets = context.state.entities
    .filter((entity) => entity.owner !== projectile.owner && !entity.dead && !entity.removed && entity.deployRemaining <= 0 && entity.movement !== 'air' && !alreadyHit.has(entity.id))
    .filter((entity) => distance(entity.position, projectile.position) <= projectile.splashRadius + entity.radius)
    .sort((left, right) => left.id - right.id);
  for (const target of targets) {
    projectile.hitEntityIds?.push(target.id);
    context.applyDamage(projectile.sourceId, projectile.owner, projectile.sourceCardId, projectile.visualKey, target, projectile.damage, projectile.towerDamageMultiplier);
    if ((projectile.knockback ?? 0) > 0 && target.kind !== 'tower' && target.movement === 'ground') {
      const direction = projectile.owner === 'player' ? -1 : 1;
      const previous = { ...target.position };
      const proposed = clampPoint({ x: previous.x, y: previous.y + direction * (projectile.knockback ?? 0) }, context.arena.worldBounds, target.radius);
      const resolution = constrainGroundStep(target, previous, proposed, context.arena, projectile.knockback ?? 0);
      target.position = resolution.position;
      context.recordMovementResolution(target, previous, proposed, resolution, 'knockback');
    }
  }
  if (targets.length) context.record('LINE_PROJECTILE_HIT', { projectileId: projectile.id, targetIds: targets.map((target) => target.id) }, { entityIds: [projectile.id, ...targets.map((target) => target.id)], cardIds: [projectile.sourceCardId] });
  if (length <= projectile.speedPerTick) impactProjectile(context, projectile);
}
